import React from 'react';
import KpiCard from '../../components/KpiCard';
import { Droplets, DollarSign, TrendingUp } from 'lucide-react';

const fmtNum = (n, d = 0) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const fmtCop = (n) => `$ ${Math.round(Number(n || 0)).toLocaleString('es-CO')}`;
const fmtPct = (n) => `${n >= 0 ? '+' : ''}${Number(n || 0).toFixed(1)}%`;

// Tarjetas hero (dosificación, costo, sobrecosto) + barra de litros reales vs. teóricos.
// Reemplaza el pictograma de gotas del HTML de referencia por una barra de progreso,
// el mismo lenguaje visual que ya usan KpisCuadreSection/DetalleComparativoSection.
const HeroKpisSection = ({ heroKpis }) => {
  const k = heroKpis;
  const dosisStatus = Math.abs(k.pctDesviacion) <= 15 ? 'ok' : 'danger';
  const costoStatus = k.sobrecostoKg > 0 ? 'danger' : 'ok';

  const pctTeorico = k.litrosRealTotal > 0 ? Math.min((k.litrosTeoricoTotal / k.litrosRealTotal) * 100, 100) : 0;
  const pctExceso = 100 - pctTeorico;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          icon={<Droplets className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Dosificación real por kilo"
          value={fmtNum(k.mlKgReal, 2)}
          unit="ml/kg"
          status={dosisStatus}
          showTag={false}
          sub={{ label: 'Teórico', value: `${fmtNum(k.mlKgTeorico, 2)} ml/kg` }}
          sub2={{ label: 'Desviación', value: fmtPct(k.pctDesviacion) }}
        />
        <KpiCard
          icon={<DollarSign className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Costo de químicos por kilo"
          value={fmtCop(k.costoKgReal)}
          status={costoStatus}
          showTag={false}
          sub={{ label: 'Teórico', value: fmtCop(k.costoKgTeorico) }}
          sub2={{ label: 'Gasto en el periodo', value: fmtCop(k.costoRealTotal) }}
        />
        <KpiCard
          icon={<TrendingUp className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Sobrecosto por kilo"
          value={`${k.sobrecostoKg >= 0 ? '+' : ''}${fmtCop(k.sobrecostoKg)}`}
          status={costoStatus}
          showTag={false}
          sub={{ label: 'Sobrecosto total', value: `${k.sobrecostoTotal >= 0 ? '+' : ''}${fmtCop(k.sobrecostoTotal)}` }}
          sub2={{ label: 'Kilos procesados', value: `${fmtNum(k.kilosProcesados)} kg` }}
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700/80 shadow-sm p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-[#001F41] dark:text-white tracking-tight">
              Litros consumidos contra litros teóricos
            </h2>
            <p className="text-[11.5px] text-[#43474F] dark:text-gray-400 mt-0.5">
              En navy lo que los programas exigían, en coral el exceso sobre lo teórico.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] font-semibold tracking-wider uppercase text-[#43474F] dark:text-gray-400">
              Costo promedio
            </div>
            <div className="text-[15px] font-bold text-[#001F41] dark:text-white mt-0.5">
              {fmtCop(k.costoPromedioLitro)} / litro
            </div>
          </div>
        </div>

        <div className="flex h-9 rounded-lg overflow-hidden mb-4">
          {pctTeorico > 0 && (
            <div
              className="bg-[#0F3460] flex items-center justify-center text-white text-[12px] font-bold"
              style={{ flex: pctTeorico }}
            >
              {fmtNum(k.litrosTeoricoTotal, 1)} L
            </div>
          )}
          {pctExceso > 0 && (
            <div
              className="bg-[#FC536D] flex items-center justify-center text-white text-[12px] font-bold"
              style={{ flex: Math.max(pctExceso, 4) }}
            >
              +{fmtNum(k.litrosRealTotal - k.litrosTeoricoTotal, 1)} L
            </div>
          )}
        </div>

        <div className="flex gap-6 flex-wrap">
          <div className="flex items-center gap-2 text-[12px] text-[#43474F] dark:text-gray-400">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#0F3460]" />
            Teórico según programas ·{' '}
            <b className="font-['Manrope'] text-[#1A1C1C] dark:text-white">
              {fmtNum(k.litrosTeoricoTotal, 1)} L · {fmtCop(k.costoTeoricoTotal)}
            </b>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-[#43474F] dark:text-gray-400">
            <span className="w-2.5 h-2.5 rounded-sm bg-[#FC536D]" />
            {k.litrosRealTotal >= k.litrosTeoricoTotal ? 'Exceso sobre lo teórico' : 'Por debajo de lo teórico'} ·{' '}
            <b className="font-['Manrope'] text-[#B71D3F]">
              {fmtNum(Math.abs(k.litrosRealTotal - k.litrosTeoricoTotal), 1)} L · {fmtCop(Math.abs(k.sobrecostoTotal))}
            </b>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroKpisSection;
